import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    const onScroll = () => {
      if (parallaxRef.current) {
        parallaxRef.current.style.transform = `translateY(${window.scrollY * 0.4}px)`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-[#f5f3ef]" id="hero">
      {/* Parallax Background */}
      <div ref={parallaxRef} className="absolute inset-0 will-change-transform">
        <img
          src="https://public.readdy.ai/ai/img_res/521339a87a5a3f1bad3c8be17913c981.jpg"
          alt="TEVANASHI Hero"
          className="w-full h-full object-cover object-center"
        />
        {/* Light overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#f5f3ef]/90 via-[#f5f3ef]/70 to-[#f5f3ef]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f5f3ef]/80 via-transparent to-[#f5f3ef]/20" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(180,160,120,0.08) 0%, transparent 60%)' }} />
      </div>

      {/* Decorative vertical line */}
      <div
        className="absolute left-[10%] top-0 bottom-0 w-px"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(180,160,120,0.4) 30%, rgba(180,160,120,0.4) 70%, transparent)' }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex flex-col justify-center min-h-screen px-6 md:px-16 lg:px-24 pt-20">
        <div className="max-w-4xl">
          {/* Eyebrow */}
          <div
            className={`flex items-center gap-4 mb-8 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="w-8 h-px bg-[#b4a078]" />
            <span className="text-[#b4a078] text-[10px] tracking-[0.4em] uppercase font-light">
              The All-Star Partner
            </span>
          </div>

          {/* Main Copy */}
          <h1
            className={`text-[#1a1a1a] font-light leading-snug mb-6 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: 'clamp(1.55rem, 4vw, 3.8rem)',
              transitionDelay: '400ms',
            }}
          >
            任せるという選択。
            <br />
            <span className="text-[#b4a078]">加速するという確信。</span>
          </h1>

          {/* Sub Copy */}
          <div
            className={`mb-10 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '600ms' }}
          >
            <p className="text-[#2d2d2d]/70 text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              新たな事業の立ち上げから成長まで、最強の専門家チームが完全バックアップ
            </p>
            <p className="text-[#2d2d2d]/50 text-xs md:text-sm leading-relaxed">7つの事業とその専門家が1つのパッケージになりました。</p>
          </div>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '800ms' }}
          >
            <a
              href="#cta"
              className="group flex items-center gap-3 bg-[#b4a078] hover:bg-[#a08e68] text-white text-xs tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap w-fit"
            >
              無料相談を予約する
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/30 transition-all">
                <i className="ri-arrow-right-line text-xs" />
              </span>
            </a>
            <a
              href="#team"
              className="flex items-center gap-2 border border-[#1a1a1a]/20 hover:border-[#1a1a1a]/50 text-[#1a1a1a]/50 hover:text-[#1a1a1a] text-xs tracking-[0.2em] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap w-fit"
            >
              チームを見る
            </a>
          </div>
        </div>

        {/* Bottom large text */}
        <div
          className={`absolute bottom-0 left-0 right-0 overflow-hidden transition-all duration-1200 ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '1000ms' }}
        >
          <p
            className="text-[#1a1a1a]/[0.04] font-black whitespace-nowrap leading-none select-none"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(5rem, 14vw, 14rem)',
              letterSpacing: '-0.02em',
            }}
          >
            ALL-STAR PARTNER
          </p>
        </div>

        {/* Scroll indicator */}
        <div
          className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}
          style={{ transitionDelay: '1200ms' }}
        >
          <span className="text-[#1a1a1a]/30 text-[9px] tracking-[0.3em] uppercase">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-[#1a1a1a]/30 to-transparent animate-pulse" />
        </div>
      </div>

      {/* Stats bar */}
      <div
        className={`absolute bottom-0 right-0 left-0 z-10 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        style={{ transitionDelay: '1000ms' }}
      >
        <div className="flex justify-end">
          <div className="flex items-stretch divide-x divide-[#1a1a1a]/8 bg-white/70 backdrop-blur-sm border-t border-l border-[#1a1a1a]/8 rounded-tl-2xl">
            {[
              { num: '20億+', label: 'EC累計売上' },
              { num: '900冠+', label: 'ランキング1位' },
              { num: '100+', label: '商品開発実績' },
              { num: '21年', label: '美容師キャリア' },
            ].map((stat) => (
              <div key={stat.label} className="px-6 py-4 text-center">
                <div className="text-[#b4a078] text-lg font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {stat.num}
                </div>
                <div className="text-[#1a1a1a]/40 text-[9px] tracking-widest mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
